import { Link } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { AppShellV8 } from "../AppShellV8";
import Button from "@/ui/Button";
import { ButtonTypes } from "@/ui/types";
import { DOCS_API_DOMAIN } from "@launchos/shared-utils/env";
import { getActiveClient, useActiveClient } from "../activeClient";
import { PersonFilter } from "../PersonFilter";
import { useRoster, useEnsureRoster } from "../roster";
import { LiveNow } from "./LiveNow";

const MEETINGS_CLIENT_SLUG = getActiveClient().slug;

type ProcessingStatus = "processing" | "processed" | "new";

interface Meeting {
  id: string;
  title: string;
  client: string | null;
  date: string;
  dateIso: string;
  time: string;
  endTime: string;
  duration: string;
  deliverables: number;
  actionItems: number;
  participants: string[];
  status: ProcessingStatus;
  dateGroup: string;
  summary: string;
}

interface UpcomingEvent {
  title: string;
  time: string;
  endTime: string;
  color: string;
  client: string | null;
}

interface UpcomingDay {
  day: number;
  month: string;
  weekday: string;
  isToday?: boolean;
  events: UpcomingEvent[];
}

const _ac = getActiveClient();
const CLIENT_CONFIG: Record<string, { initials: string; name: string; color: string; accent: string }> = {
  [_ac.slug]: { initials: _ac.initials, name: _ac.name, color: _ac.color, accent: _ac.color },
};

const PARTICIPANT_NAMES: Record<string, string> = {
  KG: "Kyle Graham",
  CF: "Claire Fabella",
  HA: "Hannah Abalos",
  RM: "Ryan Morta",
};

const PARTICIPANT_COLORS: Record<string, string> = {
  KG: "bg-blue-500",
  CF: "bg-green-500",
  HA: "bg-violet-500",
  RM: "bg-orange-400",
};

/* ── Real-data projection (operator API meeting → Meeting) ── */
const MEETING_CLIENT_KEY: Record<string, string> = { [getActiveClient().slug]: getActiveClient().slug };
const PERSON_SLUG_TO_INITIALS: Record<string, string> = {
  kyle: "KG", claire: "CF", hannah: "HA", ryan: "RM", rachel: "RX", adrienne: "AM",
};
const INITIALS_TO_PERSON_SLUG: Record<string, string> = Object.fromEntries(
  Object.entries(PERSON_SLUG_TO_INITIALS).map(([slug, initials]) => [initials, slug])
);
const fmtTime = (d: Date) => d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
const fmtDateShort = (d: Date) => d.toLocaleDateString("en-US", { month: "short", day: "numeric" });

interface ApiMeeting {
  id: string;
  title: string;
  client?: string | null;
  date?: string | null;
  duration?: number | null;
  participants?: string[];
  status?: string | null;
  summary?: string;
  deliverableCount?: number;
  actionCount?: number;
}

// AMB-415: richer date grouping than just "This Week / Earlier". Relative buckets that read
// naturally and sort newest-first: Today · Yesterday · Earlier This Week · Last Week · then
// by month (e.g. "May 2026"). Anchored to the calendar week (Mon start).
const MONTHS_LONG = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const relativeBucket = (start: Date, now: Date): string => {
  const d0 = new Date(start.getFullYear(), start.getMonth(), start.getDate()).getTime();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const DAY = 86400000;
  if (d0 === today) return "Today";
  if (d0 === today - DAY) return "Yesterday";
  const weekStart = today - ((now.getDay() + 6) % 7) * DAY; // Monday of this week
  if (d0 >= weekStart) return "Earlier This Week";
  if (d0 >= weekStart - 7 * DAY) return "Last Week";
  return `${MONTHS_LONG[start.getMonth()]} ${start.getFullYear()}`;
};

const apiToMeeting = (m: ApiMeeting): Meeting => {
  const start = m.date ? new Date(m.date) : null;
  const valid = start && !isNaN(start.getTime());
  const dur = typeof m.duration === "number" ? m.duration : 0;
  const end = valid && dur ? new Date(start!.getTime() + dur * 60000) : null;
  const now = new Date();
  const status: ProcessingStatus =
    m.status === "processing" || m.status === "new" ? m.status : "processed";
  return {
    id: m.id,
    title: m.title,
    client: MEETING_CLIENT_KEY[m.client || ""] || m.client || null,
    date: valid ? fmtDateShort(start!) : "",
    dateIso: m.date || "",
    time: valid ? fmtTime(start!) : "",
    endTime: end ? fmtTime(end) : "",
    duration: dur ? `${dur} min` : "",
    deliverables: m.deliverableCount || 0,
    actionItems: m.actionCount || 0,
    participants: (m.participants || []).map((p) => PERSON_SLUG_TO_INITIALS[p] || p.slice(0, 2).toUpperCase()),
    status,
    dateGroup: valid ? relativeBucket(start!, now) : "Undated",
    summary: m.summary || "",
  };
};

// Upcoming events are derived from real future-dated meetings at runtime
// (see the `upcomingDays` state in the Meetings component).

function StatusBadge({ status }: { status: ProcessingStatus }) {
  if (status === "processing") {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full bg-blue-50 px-2 py-0.5 text-[11px] font-medium text-blue-600">
        <span className="relative flex h-1.5 w-1.5">
          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-blue-400 opacity-75" />
          <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-blue-500" />
        </span>
        Processing
      </span>
    );
  }
  if (status === "new") {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-2 py-0.5 text-[11px] font-medium text-gray-500">
        New
      </span>
    );
  }
  return null;
}

function MeetingIcon({ client }: { client: string | null }) {
  if (client && CLIENT_CONFIG[client]) {
    const cfg = CLIENT_CONFIG[client];
    return (
      <span className={`flex h-9 w-9 items-center justify-center rounded-lg ${cfg.color} text-[10px] font-bold text-white`}>
        {cfg.initials}
      </span>
    );
  }
  return (
    <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-gray-200 text-gray-500">
      <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
      </svg>
    </span>
  );
}

function OutputCluster({ meeting }: { meeting: Meeting }) {
  // Unreviewed/unresolved counts: anything not yet processed needs attention on its full output.
  const needsAttention = meeting.status !== "processed";
  const unresolvedActions = needsAttention ? meeting.actionItems : 0;
  const unresolvedDeliverables = needsAttention ? meeting.deliverables : 0;

  if (meeting.actionItems === 0 && meeting.deliverables === 0) {
    return <span className="hidden shrink-0 text-xs text-gray-300 sm:inline">No output yet</span>;
  }

  return (
    <div className="hidden shrink-0 items-center gap-3 sm:flex">
      {/* Action items */}
      {meeting.actionItems > 0 && (
        <span
          className={`inline-flex items-center gap-1 text-xs tabular-nums ${
            unresolvedActions > 0 ? "font-medium text-amber-600" : "text-gray-500"
          }`}
          title={
            unresolvedActions > 0
              ? `${unresolvedActions} action items need review`
              : `${meeting.actionItems} action items`
          }
        >
          {/* checklist icon */}
          <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          {meeting.actionItems} <span className="text-gray-400">actions</span>
        </span>
      )}

      {/* Deliverables */}
      {meeting.deliverables > 0 && (
        <span
          className={`inline-flex items-center gap-1 text-xs tabular-nums ${
            unresolvedDeliverables > 0 ? "font-medium text-amber-600" : "text-gray-500"
          }`}
          title={
            unresolvedDeliverables > 0
              ? `${unresolvedDeliverables} deliverables awaiting review`
              : `${meeting.deliverables} deliverables`
          }
        >
          {/* document icon */}
          <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
          </svg>
          {meeting.deliverables} <span className="text-gray-400">deliverables</span>
        </span>
      )}
    </div>
  );
}

interface MenuItem {
  label: string;
  onClick: () => void;
  destructive?: boolean;
}

// Shared ⋯ overflow menu — fixed/portal-positioned popover behind a click-outside backdrop so it is never clipped.
function OverflowMenu({ items, alwaysVisible = false }: { items: MenuItem[]; alwaysVisible?: boolean }) {
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState<{ top: number; left: number }>({ top: 0, left: 0 });
  const btnRef = useRef<HTMLButtonElement>(null);

  const toggle = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const rect = btnRef.current?.getBoundingClientRect();
    if (rect) {
      // Anchor the menu's right edge under the button, opening downward.
      setCoords({ top: rect.bottom + 4, left: rect.right - 160 });
    }
    setOpen((v) => !v);
  };

  return (
    <>
      <button
        ref={btnRef}
        onClick={toggle}
        aria-label="More options"
        className={`flex h-7 w-7 shrink-0 items-center justify-center rounded text-gray-400 transition-all duration-150 hover:bg-gray-100 hover:text-gray-600 ${
          alwaysVisible ? "" : "opacity-0 group-hover:opacity-100"
        } ${open ? "opacity-100 bg-gray-100" : ""}`}
      >
        <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24">
          <path d="M12 6.5a1.5 1.5 0 100-3 1.5 1.5 0 000 3zM12 13.5a1.5 1.5 0 100-3 1.5 1.5 0 000 3zM12 20.5a1.5 1.5 0 100-3 1.5 1.5 0 000 3z" />
        </svg>
      </button>
      {open &&
        createPortal(
          <>
            <div className="fixed inset-0 z-[60]" onClick={(e) => { e.stopPropagation(); setOpen(false); }} />
            <div
              className="fixed z-[61] w-40 rounded-lg bg-white py-1 text-[13px] shadow-xl ring-1 ring-black/[0.06]"
              style={{ top: coords.top, left: coords.left }}
              onClick={(e) => e.stopPropagation()}
            >
              {items.map((item, i) => (
                <button
                  key={i}
                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); setOpen(false); item.onClick(); }}
                  className={`flex w-full items-center px-3 py-1.5 text-left transition-all duration-150 hover:bg-gray-50 ${
                    item.destructive ? "text-red-600 hover:bg-red-50" : "text-gray-700"
                  }`}
                >
                  {item.label}
                </button>
              ))}
            </div>
          </>,
          document.body
        )}
    </>
  );
}

// Bottom toast with an Undo affordance, used by soft-delete (Archive) flows.
function UndoToast({ message, onUndo, onDismiss }: { message: string; onUndo: () => void; onDismiss: () => void }) {
  useEffect(() => {
    const t = setTimeout(onDismiss, 5000);
    return () => clearTimeout(t);
  }, [onDismiss]);
  return (
    <div className="fixed bottom-6 left-1/2 z-[70] flex -translate-x-1/2 items-center gap-3 rounded-lg bg-gray-900 px-4 py-2.5 text-[13px] text-white shadow-xl">
      <span>{message}</span>
      <button
        onClick={onUndo}
        className="font-semibold text-blue-300 transition-all duration-150 hover:text-blue-200 active:scale-[0.97]"
      >
        Undo
      </button>
    </div>
  );
}

export function Meetings() {
  // AMB-414: the meetings list reacts to the LIVE top-bar client (was a module-level
  // constant frozen at import → list stuck on whichever client loaded first).
  const activeSlug = useActiveClient().slug;
  const [search, setSearch] = useState("");
  const [personFilter, setPersonFilter] = useState("all");
  // Person filter options come from THIS tenant's roster (same as Tasks/Calendar, AMB-438),
  // never the hardcoded Ambid team. Meeting participants are keyed by initials.
  useEnsureRoster();
  const roster = useRoster();
  const [showUpload, setShowUpload] = useState(false);
  const [upcomingOpen, setUpcomingOpen] = useState(true);

  // ── Upload-transcript modal state (BUG-08) ──
  const [clients, setClients] = useState<{ slug: string; name: string }[]>([]);
  const [upFile, setUpFile] = useState<File | null>(null);
  const [upTitle, setUpTitle] = useState("");
  const [upDate, setUpDate] = useState("");
  const [upTime, setUpTime] = useState(""); // AMB-356 #1: the real meeting start time (optional)
  const [upClient, setUpClient] = useState(MEETINGS_CLIENT_SLUG); // default to the active client
  const [uploading, setUploading] = useState(false);
  const [uploadStatus, setUploadStatus] = useState<{ kind: "error" | "success"; msg: string } | null>(null);

  // 08b: the assign-to-client picker needs the real client list, not just the active one.
  useEffect(() => {
    let cancelled = false;
    fetch(`${DOCS_API_DOMAIN}/api/agents/clients`)
      .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
      .then((d) => { if (!cancelled) setClients(Array.isArray(d?.items) ? d.items : []); })
      .catch(() => {/* leave empty on failure */});
    return () => { cancelled = true; };
  }, []);

  const ALLOWED_UPLOAD_EXT = [".txt", ".md", ".vtt", ".srt"];
  const submitUpload = async () => {
    if (!upFile) return setUploadStatus({ kind: "error", msg: "Choose a transcript file." });
    if (!ALLOWED_UPLOAD_EXT.some((e) => upFile.name.toLowerCase().endsWith(e)))
      return setUploadStatus({ kind: "error", msg: "Text files only: .txt, .md, .vtt, .srt" });
    if (!upTitle.trim()) return setUploadStatus({ kind: "error", msg: "Add a title." });
    if (!upDate) return setUploadStatus({ kind: "error", msg: "Pick a date." });
    if (!upClient) return setUploadStatus({ kind: "error", msg: "Choose a client." });
    setUploading(true);
    setUploadStatus(null);
    try {
      const content = await upFile.text();
      const res = await fetch(`${DOCS_API_DOMAIN}/api/agents/transcript/upload`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ client: upClient, title: upTitle.trim(), date: upDate, time: upTime || undefined, filename: upFile.name, content }),
      });
      if (!res.ok) {
        const e = await res.json().catch(() => ({}));
        throw new Error(e.error || `Upload failed (${res.status})`);
      }
      setUploading(false);
      setUploadStatus({ kind: "success", msg: "Uploaded — processing. Your brief will appear in Meetings shortly." });
      setUpFile(null); setUpTitle(""); setUpDate(""); setUpClient(MEETINGS_CLIENT_SLUG);
      // show the label briefly, then close
      setTimeout(() => { setShowUpload(false); setUploadStatus(null); }, 1800);
    } catch (e) {
      setUploading(false);
      setUploadStatus({ kind: "error", msg: (e as Error).message });
    }
  };

  // ── CRUD local state ──
  const [meetings, setMeetings] = useState<Meeting[]>([]);
  const [meetingsLoading, setMeetingsLoading] = useState(true);
  // Upcoming = real future-dated meetings (empty until Google Calendar sync lands;
  // gcal-synced future meetings flow through the same /meetings endpoint).
  const [upcomingDays, setUpcomingDays] = useState<UpcomingDay[]>([]);
  const [archived, setArchived] = useState<Set<string>>(new Set());
  const [toast, setToast] = useState<{ message: string; undo: () => void } | null>(null);
  const [editing, setEditing] = useState<Meeting | null>(null);
  const [editDraft, setEditDraft] = useState<{ title: string; participants: string; date: string; time: string }>({
    title: "", participants: "", date: "", time: "",
  });

  // Load real meetings from the operator API; replaces the mock on success.
  // Refetches whenever the active client changes (AMB-414).
  useEffect(() => {
    let cancelled = false;
    setMeetingsLoading(true);
    fetch(`${DOCS_API_DOMAIN}/api/agents/meetings?client=${activeSlug}`)
      .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
      .then((data) => {
        if (cancelled) return;
        // Empty result for this client → clear (don't keep the previous client's list).
        if (!data?.items?.length) { setMeetings([]); setUpcomingDays([]); return; }
        setMeetings(data.items.map(apiToMeeting));

        // Build the Upcoming panel from future-dated meetings, grouped by day.
        const now = new Date();
        const byDay = new Map<string, UpcomingDay>();
        for (const m of data.items as ApiMeeting[]) {
          if (!m.date) continue;
          const start = new Date(m.date);
          if (isNaN(start.getTime()) || start.getTime() <= now.getTime()) continue;
          const key = start.toDateString();
          if (!byDay.has(key)) {
            byDay.set(key, {
              day: start.getDate(),
              month: start.toLocaleDateString("en-US", { month: "short" }),
              weekday: start.toLocaleDateString("en-US", { weekday: "short" }).toUpperCase(),
              events: [],
            });
          }
          const dur = typeof m.duration === "number" ? m.duration : 0;
          const end = dur ? new Date(start.getTime() + dur * 60000) : null;
          byDay.get(key)!.events.push({
            title: m.title,
            time: fmtTime(start),
            endTime: end ? fmtTime(end) : "",
            color: CLIENT_CONFIG[MEETING_CLIENT_KEY[m.client || ""] || ""]?.accent || "bg-blue-500",
            client: m.client || null,
          });
        }
        setUpcomingDays(Array.from(byDay.values()));
      })
      .catch(() => {/* keep mock fallback */})
      .finally(() => { if (!cancelled) setMeetingsLoading(false); });
    return () => {
      cancelled = true;
    };
  }, [activeSlug]);

  // Persist a meeting field update to the vault.
  const postMeetingUpdate = (id: string, fields: Record<string, unknown>) =>
    fetch(`${DOCS_API_DOMAIN}/api/agents/entity/update`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ client: MEETINGS_CLIENT_SLUG, type: "meeting", id, fields }),
    }).catch(() => {/* optimistic */});

  const archiveMeeting = (m: Meeting) => {
    setArchived((prev) => new Set(prev).add(m.id));
    postMeetingUpdate(m.id, { status: "archived" });
    setToast({
      message: `Archived "${m.title}" · `,
      undo: () => {
        setArchived((prev) => {
          const next = new Set(prev);
          next.delete(m.id);
          return next;
        });
        postMeetingUpdate(m.id, { status: "processed" });
        setToast(null);
      },
    });
  };

  const openEdit = (m: Meeting) => {
    setEditing(m);
    // Seed date/time from the raw ISO so the date/time inputs round-trip cleanly.
    setEditDraft({ title: m.title, participants: m.participants.join(", "), date: m.dateIso.slice(0, 10), time: m.dateIso.slice(11, 16) });
  };

  const saveEdit = () => {
    if (!editing) return;
    const id = editing.id;
    const participantInitials = editDraft.participants.split(",").map((p) => p.trim()).filter(Boolean);
    // Recombine date + time into an ISO datetime for the frontmatter.
    const iso = editDraft.date ? `${editDraft.date}T${editDraft.time || "00:00"}:00` : "";
    const dt = iso ? new Date(iso) : null;
    const valid = dt && !isNaN(dt.getTime());
    setMeetings((prev) =>
      prev.map((m) =>
        m.id === id
          ? {
              ...m,
              title: editDraft.title,
              participants: participantInitials,
              dateIso: valid ? iso : m.dateIso,
              date: valid ? fmtDateShort(dt!) : m.date,
              time: valid ? fmtTime(dt!) : m.time,
            }
          : m
      )
    );
    const fields: Record<string, unknown> = {
      title: editDraft.title,
      participants: participantInitials.map((i) => INITIALS_TO_PERSON_SLUG[i] || i.toLowerCase()),
    };
    if (valid) fields.date = iso;
    postMeetingUpdate(id, fields);
    setEditing(null);
  };

  const upcomingCount = upcomingDays.reduce((sum, d) => sum + d.events.length, 0);

  const filtered = meetings.filter((m) => {
    if (archived.has(m.id)) return false;
    // Client scoping is driven by the top-bar client selector (server-side fetch), not a
    // redundant local dropdown (AMB-412).
    if (personFilter !== "all" && !m.participants.includes(personFilter)) return false;
    if (search && !m.title.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  // Group by each meeting's actual dateGroup, in first-seen (date-sorted) order, so
  // arbitrary real-data groups ("This Week", "Earlier", …) render — not just a fixed list.
  const groupedMeetings = (() => {
    const order: string[] = [];
    const byGroup = new Map<string, Meeting[]>();
    for (const m of filtered) {
      if (!byGroup.has(m.dateGroup)) {
        byGroup.set(m.dateGroup, []);
        order.push(m.dateGroup);
      }
      byGroup.get(m.dateGroup)!.push(m);
    }
    return order.map((label) => ({ label, meetings: byGroup.get(label)! }));
  })();

  return (
    <AppShellV8
      activeApp="meetings"
      breadcrumb={["Meetings"]}
      reviewCount={7}
      headerActions={
        <Button label="Upload Transcript" type={ButtonTypes.OUTLINED} onClick={() => setShowUpload(true)} />
      }
    >
      <div className="flex h-full flex-col">
        {/* ── Toolbar ── */}
        <div className="flex flex-wrap items-center gap-3 border-b border-black/[0.06] px-6 py-3">
          <div className="flex flex-1 items-center gap-2">
            <div className="relative max-w-xs flex-1">
              <svg className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
              </svg>
              <input
                type="text" placeholder="Search meetings..." value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full rounded-lg border border-black/[0.06] py-1.5 pl-8 pr-3 text-[13px] text-gray-700 placeholder:text-gray-400 transition-all duration-150 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </div>
          </div>
          {/* AMB-411: the shared person filter ("Showing meetings for"), right-aligned,
              replacing the bare "All People" select. The redundant local All-Clients
              dropdown is gone (AMB-412) — client scoping is the top-bar selector. */}
          <PersonFilter
            className="ml-auto"
            label="Showing meetings for"
            value={personFilter}
            onChange={setPersonFilter}
            options={roster.map((m) => ({ id: m.initials, name: m.name }))}
          />
        </div>

        {/* ── Content ── */}
        <div className="flex-1 overflow-auto">
          <div className="mx-auto max-w-3xl px-6 py-6">

            {/* ── Live now (LMR-5): meetings being recorded, opening their live room ── */}
            <LiveNow />

            {/* ── Coming Up (compact, collapsible) ── */}
            <div className="mb-6">
              <div className="mb-2.5 flex items-center justify-between">
                <button
                  onClick={() => setUpcomingOpen((v) => !v)}
                  className="group flex items-center gap-1.5 rounded-lg py-0.5 pr-2 text-left transition-all duration-150"
                >
                  <svg
                    className={`h-4 w-4 text-gray-400 transition-transform duration-150 ${upcomingOpen ? "rotate-90" : ""}`}
                    fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
                  </svg>
                  <h2 className="text-[13px] font-semibold tracking-tight text-gray-900 group-hover:text-gray-700">Coming up</h2>
                  <span className="text-xs text-gray-400">· {upcomingCount} this week</span>
                </button>
                <div className={`flex items-center gap-1 ${upcomingOpen ? "" : "hidden"}`}>
                  <button className="flex h-6 w-6 items-center justify-center rounded-lg text-gray-400 transition-all duration-150 hover:bg-gray-100 hover:text-gray-600">
                    <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" /></svg>
                  </button>
                  <button className="flex h-6 w-6 items-center justify-center rounded-lg text-gray-400 transition-all duration-150 hover:bg-gray-100 hover:text-gray-600">
                    <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" /></svg>
                  </button>
                </div>
              </div>

              {upcomingOpen && (
                <div className="overflow-hidden rounded-xl bg-gray-50/80 ring-1 ring-black/[0.04]">
                  {upcomingDays.length === 0 && (
                    <p className="px-4 py-3 text-xs text-gray-400">No upcoming meetings scheduled. Future meetings appear here once Google Calendar is connected.</p>
                  )}
                  {upcomingDays.map((day, i) => (
                    <div key={i} className={`flex items-center gap-4 px-4 py-2 ${i > 0 ? "border-t border-black/[0.04]" : ""}`}>
                      {/* Date column (compact) */}
                      <div className="flex w-16 shrink-0 items-baseline gap-1">
                        <span className="text-base font-semibold tabular-nums text-gray-900">{day.day}</span>
                        <span className="flex items-center gap-0.5 text-[11px] font-medium text-gray-500">
                          {day.weekday}
                          {day.isToday && <span className="ml-0.5 inline-block h-1.5 w-1.5 rounded-full bg-red-500" />}
                        </span>
                      </div>

                      {/* Events (single tight row, wrapping) */}
                      <div className="flex flex-1 flex-wrap items-center gap-x-4 gap-y-1">
                        {day.events.length === 0 ? (
                          <p className="text-xs text-gray-400">No more events today</p>
                        ) : (
                          day.events.map((evt, j) => (
                            <div key={j} className="flex items-center gap-2">
                              <div className={`h-4 w-[3px] rounded-full ${evt.color}`} />
                              <span className="text-[13px] font-medium text-gray-900">{evt.title}</span>
                              <span className="text-xs text-gray-400">{evt.time}–{evt.endTime}</span>
                            </div>
                          ))
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* ── Past Meetings ── */}
            {meetingsLoading ? (
              <div className="flex flex-col items-center justify-center py-16 text-center">
                <p className="text-[13px] text-gray-400">Loading meetings…</p>
              </div>
            ) : groupedMeetings.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 text-center">
                <svg className="mb-3 h-10 w-10 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 20.25c4.97 0 9-3.694 9-8.25s-4.03-8.25-9-8.25S3 7.444 3 12c0 2.104.859 4.023 2.273 5.48.432.447.74 1.04.586 1.641a4.483 4.483 0 01-.923 1.785A5.969 5.969 0 006 21c1.282 0 2.47-.402 3.445-1.087.81.22 1.668.337 2.555.337z" />
                </svg>
                <p className="text-[13px] text-gray-500">No meetings match your filters</p>
                <p className="mt-1 text-xs text-gray-400">Try adjusting your search or filter criteria</p>
              </div>
            ) : (
              <div className="space-y-6">
                {groupedMeetings.map((group) => (
                  <div key={group.label}>
                    <h3 className="mb-2 text-[13px] font-semibold text-gray-500">{group.label}</h3>
                    <div className="space-y-0.5">
                      {group.meetings.map((meeting) => {
                        const participantNames = meeting.participants
                          .map((p) => {
                            if (p === "KG") return "Me";
                            const full = PARTICIPANT_NAMES[p];
                            return full || p;
                          })
                          .join(", ");

                        return (
                          <Link
                            key={meeting.id}
                            to={`/agents/digest?id=${encodeURIComponent(meeting.id)}`}
                            className="group relative flex items-center gap-3.5 rounded-lg px-2.5 py-3 transition-all duration-150 hover:bg-gray-50 hover:ring-1 hover:ring-black/[0.04]"
                          >
                            {/* Hover tooltip */}
                            <div className="pointer-events-none absolute left-14 right-20 top-full z-20 mt-0.5 opacity-0 transition-all duration-200 delay-300 group-hover:opacity-100">
                              <div className="rounded-lg bg-white px-3 py-2 shadow-lg ring-1 ring-black/[0.06]">
                                <p className="text-xs leading-relaxed text-gray-600">{meeting.summary}</p>
                              </div>
                            </div>

                            {/* Icon */}
                            <MeetingIcon client={meeting.client} />

                            {/* Title + participants */}
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center gap-2">
                                <p className="truncate text-[13px] font-medium text-gray-900">{meeting.title}</p>
                                <StatusBadge status={meeting.status} />
                              </div>
                              <p className="mt-0.5 truncate text-xs text-gray-400">{participantNames}</p>
                            </div>

                            {/* Avatars (compact) */}
                            <div className="hidden shrink-0 lg:flex">
                              <div className="flex -space-x-1">
                                {meeting.participants.slice(0, 3).map((p) => (
                                  <span key={p} title={PARTICIPANT_NAMES[p] || p}
                                    className={`flex h-6 w-6 items-center justify-center rounded-full ${PARTICIPANT_COLORS[p] || "bg-gray-300"} text-[7px] font-bold text-white ring-2 ring-white`}
                                  >{p}</span>
                                ))}
                                {meeting.participants.length > 3 && (
                                  <span className="flex h-6 w-6 items-center justify-center rounded-full bg-gray-200 text-[8px] font-medium text-gray-500 ring-2 ring-white">
                                    +{meeting.participants.length - 3}
                                  </span>
                                )}
                              </div>
                            </div>

                            {/* Output cluster: actions + deliverables, amber when unreviewed */}
                            <OutputCluster meeting={meeting} />

                            {/* Date + time → swaps to a "View digest" affordance on hover (AMB-392) */}
                            <div className="relative w-24 shrink-0 text-right">
                              <div className="transition-opacity duration-150 group-hover:opacity-0">
                                {meeting.date && <span className="block text-xs font-medium tabular-nums text-gray-600">{meeting.date}</span>}
                                <span className="block text-[11px] tabular-nums text-gray-400">{meeting.time}</span>
                              </div>
                              <span className="absolute inset-y-0 right-0 flex items-center justify-end gap-0.5 text-xs font-medium text-blue-600 opacity-0 transition-opacity duration-150 group-hover:opacity-100">
                                View digest
                                <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                  <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
                                </svg>
                              </span>
                            </div>

                            {/* ⋯ overflow menu */}
                            <OverflowMenu
                              items={[
                                { label: "Edit details", onClick: () => openEdit(meeting) },
                                { label: "Archive", onClick: () => archiveMeeting(meeting), destructive: true },
                              ]}
                            />
                          </Link>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* ── Upload modal ── */}
        {showUpload && (
          <>
            <div className="fixed inset-0 z-50 bg-black/20 backdrop-blur-[2px]" onClick={() => setShowUpload(false)} />
            <div className="fixed left-1/2 top-1/2 z-50 w-full max-w-md -translate-x-1/2 -translate-y-1/2 rounded-xl bg-white p-6 shadow-2xl ring-1 ring-black/[0.06]">
              <div className="mb-5 flex items-center justify-between">
                <h2 className="text-base font-semibold text-gray-900">Upload Transcript</h2>
                <button onClick={() => setShowUpload(false)} className="flex h-7 w-7 items-center justify-center rounded-lg text-gray-400 transition-all duration-150 hover:bg-gray-100 hover:text-gray-600">
                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
                </button>
              </div>
              {/* File picker — drag-drop + browse, text transcripts only (08a) */}
              <label
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => { e.preventDefault(); const f = e.dataTransfer.files?.[0]; if (f) setUpFile(f); }}
                className="mb-4 flex cursor-pointer flex-col items-center rounded-xl border-2 border-dashed border-gray-300 bg-gray-50/50 py-10 text-center transition-all duration-150 hover:border-blue-400 hover:bg-blue-50/30"
              >
                <input type="file" accept=".txt,.md,.vtt,.srt" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) setUpFile(f); }} />
                <svg className="mb-3 h-10 w-10 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5" />
                </svg>
                {upFile ? (
                  <p className="text-[13px] font-medium text-gray-800">{upFile.name}</p>
                ) : (
                  <p className="text-[13px] font-medium text-gray-600">Drop transcript file here, or <span className="text-blue-600 hover:underline">browse</span></p>
                )}
                <p className="mt-1 text-xs text-gray-400">Text only — .txt, .md, .vtt, .srt</p>
              </label>
              <div className="mb-3">
                <label className="mb-1.5 block text-xs font-medium text-gray-500">Title</label>
                <input type="text" value={upTitle} onChange={(e) => setUpTitle(e.target.value)} placeholder="e.g. Discovery Call — June 13" className="w-full rounded-lg border border-black/[0.06] px-3 py-2 text-[13px] text-gray-700 focus:outline-none focus:ring-1 focus:ring-blue-500" />
              </div>
              <div className="mb-3 flex gap-3">
                <div className="flex-1">
                  <label className="mb-1.5 block text-xs font-medium text-gray-500">Date</label>
                  <input type="date" value={upDate} onChange={(e) => setUpDate(e.target.value)} className="w-full rounded-lg border border-black/[0.06] px-3 py-2 text-[13px] text-gray-700 focus:outline-none focus:ring-1 focus:ring-blue-500" />
                </div>
                <div className="w-32">
                  {/* AMB-356 #1: the real start time. Optional — if left blank the pipeline
                      infers it from when the transcript is ingested. */}
                  <label className="mb-1.5 block text-xs font-medium text-gray-500">Start time</label>
                  <input type="time" value={upTime} onChange={(e) => setUpTime(e.target.value)} className="w-full rounded-lg border border-black/[0.06] px-3 py-2 text-[13px] text-gray-700 focus:outline-none focus:ring-1 focus:ring-blue-500" />
                </div>
              </div>
              <div className="mb-4">
                <label className="mb-1.5 block text-xs font-medium text-gray-500">Assign to client</label>
                <select value={upClient} onChange={(e) => setUpClient(e.target.value)} className="w-full rounded-lg border border-black/[0.06] px-3 py-2 text-[13px] text-gray-600 transition-all duration-150 hover:border-gray-300 focus:outline-none focus:ring-1 focus:ring-blue-500">
                  <option value="">Select client…</option>
                  {/* AMB-413: an Internal meeting (the AmbidTech scrum) — no client. */}
                  <option value="internal">Internal meeting</option>
                  {clients.map((c) => (<option key={c.slug} value={c.slug}>{c.name}</option>))}
                </select>
              </div>
              {uploadStatus && (
                <p className={`mb-3 text-[13px] ${uploadStatus.kind === "error" ? "text-red-600" : "text-emerald-600"}`}>{uploadStatus.msg}</p>
              )}
              <div className="flex justify-end gap-2">
                <button onClick={() => setShowUpload(false)} className="rounded-lg px-3 py-1.5 text-[13px] text-gray-500 transition-all duration-150 hover:bg-gray-100">Cancel</button>
                <button onClick={submitUpload} disabled={uploading} className="rounded-lg bg-gray-900 px-4 py-1.5 text-[13px] font-medium text-white shadow-sm transition-all duration-150 hover:bg-gray-800 active:scale-[0.97] disabled:opacity-50">{uploading ? "Uploading…" : "Upload"}</button>
              </div>
            </div>
          </>
        )}

        {/* ── Edit meeting modal ── */}
        {editing && (
          <>
            <div className="fixed inset-0 z-50 bg-black/20 backdrop-blur-[2px]" onClick={() => setEditing(null)} />
            <div className="fixed left-1/2 top-1/2 z-50 w-full max-w-md -translate-x-1/2 -translate-y-1/2 rounded-xl bg-white p-6 shadow-2xl ring-1 ring-black/[0.06]">
              <div className="mb-5 flex items-center justify-between">
                <h2 className="text-base font-semibold text-gray-900">Edit meeting</h2>
                <button onClick={() => setEditing(null)} className="flex h-7 w-7 items-center justify-center rounded-lg text-gray-400 transition-all duration-150 hover:bg-gray-100 hover:text-gray-600">
                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
                </button>
              </div>
              <div className="space-y-4">
                <div>
                  <label className="mb-1.5 block text-xs font-medium text-gray-500">Title</label>
                  <input
                    type="text" value={editDraft.title}
                    onChange={(e) => setEditDraft((d) => ({ ...d, title: e.target.value }))}
                    className="w-full rounded-lg border border-black/[0.06] px-3 py-2 text-[13px] text-gray-700 transition-all duration-150 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="mb-1.5 block text-xs font-medium text-gray-500">Participants (comma-separated)</label>
                  <input
                    type="text" value={editDraft.participants}
                    onChange={(e) => setEditDraft((d) => ({ ...d, participants: e.target.value }))}
                    className="w-full rounded-lg border border-black/[0.06] px-3 py-2 text-[13px] text-gray-700 transition-all duration-150 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  />
                </div>
                <div className="flex gap-3">
                  <div className="flex-1">
                    <label className="mb-1.5 block text-xs font-medium text-gray-500">Date</label>
                    <input
                      type="date" value={editDraft.date}
                      onChange={(e) => setEditDraft((d) => ({ ...d, date: e.target.value }))}
                      className="w-full rounded-lg border border-black/[0.06] px-3 py-2 text-[13px] text-gray-700 transition-all duration-150 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                    />
                  </div>
                  <div className="flex-1">
                    <label className="mb-1.5 block text-xs font-medium text-gray-500">Time</label>
                    <input
                      type="time" value={editDraft.time}
                      onChange={(e) => setEditDraft((d) => ({ ...d, time: e.target.value }))}
                      className="w-full rounded-lg border border-black/[0.06] px-3 py-2 text-[13px] text-gray-700 transition-all duration-150 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                    />
                  </div>
                </div>
              </div>
              <div className="mt-6 flex justify-end gap-2">
                <button onClick={() => setEditing(null)} className="rounded-lg px-3 py-1.5 text-[13px] text-gray-500 transition-all duration-150 hover:bg-gray-100">Cancel</button>
                <button onClick={saveEdit} className="rounded-lg bg-gray-900 px-4 py-1.5 text-[13px] font-medium text-white shadow-sm transition-all duration-150 hover:bg-gray-800 active:scale-[0.97]">Save</button>
              </div>
            </div>
          </>
        )}

        {/* ── Undo toast ── */}
        {toast && (
          <UndoToast message={toast.message} onUndo={toast.undo} onDismiss={() => setToast(null)} />
        )}
      </div>
    </AppShellV8>
  );
}
